import React from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  Button,
  Input,
  FormControl,
  FormLabel,
  Spinner,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Tooltip,
  Badge,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';
import dayjs from 'dayjs';
import Editor from 'components/editor/index';
import { convertToRaw } from 'draft-js';

var draftToHtml;

export default function SchoolComunicacion() {
  const { user } = useAuth();
  const toast = useToast();
  const schoolId = user?.school?.id;

  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);

  const [title, setTitle] = React.useState('');
  const [editorState, setEditorState] = React.useState('');

  React.useEffect(() => {
    const loadDraft = async () => {
      draftToHtml = await import('draftjs-to-html');
    };
    loadDraft();
  }, []);

  React.useEffect(() => {
    if (!schoolId) return;
    const fetchPosts = async () => {
      try {
        const res = await xhr(
          `/institutional-posts?filters[school][id][$eq]=${schoolId}&sort[0]=createdAt:DESC&pagination[pageSize]=50`
        );
        setPosts(res.data?.data || []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [schoolId]);

  const handleCreate = async () => {
    if (!title.trim()) {
      toast({ title: 'El titulo es obligatorio', status: 'warning', position: 'bottom', duration: 3000, isClosable: true });
      return;
    }

    if (!editorState) {
      toast({ title: 'El contenido es obligatorio', status: 'warning', position: 'bottom', duration: 3000, isClosable: true });
      return;
    }

    try {
      setCreating(true);
      const rawContentState = convertToRaw(editorState.getCurrentContent());
      const content = draftToHtml.default(rawContentState);

      if (rawContentState.blocks[0] && rawContentState.blocks[0].text === '') {
        toast({ title: 'El contenido no puede estar vacio', status: 'warning', position: 'bottom', duration: 3000, isClosable: true });
        return;
      }

      const res = await xhr.post('/institutional-posts', {
        data: {
          title: title.trim(),
          content,
          global: true,
        },
      });

      setPosts([res.data.data, ...posts]);
      setTitle('');
      setEditorState('');
      setShowForm(false);
      toast({ title: 'Noticia publicada correctamente', status: 'success', position: 'bottom', duration: 3000, isClosable: true });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error al publicar',
        description: error.response?.data?.error?.message || 'Intenta de nuevo',
        status: 'error',
        position: 'bottom',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Estas seguro de eliminar esta noticia?')) return;
    try {
      await xhr.delete(`/institutional-posts/${postId}`);
      setPosts(posts.filter((p) => p.id !== postId));
      toast({ title: 'Noticia eliminada', status: 'info', position: 'bottom', duration: 3000, isClosable: true });
    } catch (error) {
      console.error(error);
      toast({ title: 'Error al eliminar', status: 'error', position: 'bottom', duration: 3000, isClosable: true });
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={2}>
        <Heading size="lg">Pizarrón de noticias</Heading>
        {!showForm && (
          <Button
            leftIcon={<FiPlus />}
            colorScheme="purple"
            bg="#574feb"
            size="sm"
            onClick={() => setShowForm(true)}
          >
            Nueva noticia
          </Button>
        )}
      </Flex>
      <Text color="gray.500" fontSize="sm" mb={6}>
        Publica noticias y avisos importantes que serán visibles para todos los estudiantes de tu institución en su plataforma.
      </Text>

      {showForm && (
        <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" p={5} mb={6}>
          <Text fontWeight="600" mb={4}>Publicar noticia</Text>

          <FormControl mb={4} isRequired>
            <FormLabel>Titulo</FormLabel>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: Inicio de clases, Aviso importante..."
            />
          </FormControl>

          <FormControl mb={4} isRequired>
            <FormLabel>Contenido</FormLabel>
            <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md">
              <Editor editorState={editorState} handleEditorChange={setEditorState} />
            </Box>
          </FormControl>

          <Flex gap={3}>
            <Button variant="ghost" onClick={() => { setShowForm(false); setTitle(''); setEditorState(''); }}>
              Cancelar
            </Button>
            <Button
              colorScheme="purple"
              bg="#574feb"
              onClick={handleCreate}
              isLoading={creating}
            >
              Publicar
            </Button>
          </Flex>
        </Box>
      )}

      <Box bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" p={5}>
        {loading ? (
          <Box textAlign="center" py={8}>
            <Spinner />
          </Box>
        ) : posts.length === 0 ? (
          <Box
            textAlign="center"
            py={8}
            color="gray.400"
            border="1px dashed"
            borderColor="gray.200"
            borderRadius="lg"
          >
            <Text fontSize="sm">No hay noticias publicadas</Text>
            <Text fontSize="xs" mt={1}>
              Las noticias apareceran en el pizarron de los estudiantes
            </Text>
          </Box>
        ) : (
          <TableContainer>
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Titulo</Th>
                  <Th>Fecha</Th>
                  <Th textAlign="right">Acciones</Th>
                </Tr>
              </Thead>
              <Tbody>
                {posts.map((post) => {
                  const attrs = post.attributes || post;
                  return (
                    <Tr key={post.id}>
                      <Td fontWeight="500">{attrs.title}</Td>
                      <Td fontSize="sm" color="gray.500">
                        {attrs.createdAt ? dayjs(attrs.createdAt).format('DD/MM/YYYY') : '-'}
                      </Td>
                      <Td textAlign="right">
                        <Tooltip label="Eliminar">
                          <IconButton
                            icon={<FiTrash2 />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDelete(post.id)}
                            aria-label="Eliminar noticia"
                          />
                        </Tooltip>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
